import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  CheckCircle2,
  CopyPlus,
  Library,
  Plus,
  Save,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import SEO from "@/components/site/SEO";
import lmsApi from "@/lib/lmsApi";

function newId(prefix = "q") {
  if (
    typeof crypto !== "undefined" &&
    crypto.randomUUID
  ) {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random()
    .toString(16)
    .slice(2)}`;
}

function blankQuestion(
  type = "single_choice"
) {
  if (type === "true_false") {
    return {
      id: newId(),
      question_type: "true_false",
      prompt: "",
      options: [
        {
          id: "true",
          label: "True",
        },
        {
          id: "false",
          label: "False",
        },
      ],
      correct_answers: ["true"],
      marks: 1,
      feedback_correct: "",
      feedback_incorrect: "",
      explanation: "",
    };
  }

  return {
    id: newId(),
    question_type: type,
    prompt: "",
    options: [
      {
        id: newId("opt"),
        label: "",
      },
      {
        id: newId("opt"),
        label: "",
      },
    ],
    correct_answers: [],
    marks: 1,
    feedback_correct: "",
    feedback_incorrect: "",
    explanation: "",
  };
}

function fromApiQuestion(question) {
  const feedback =
    question.feedback || {};

  return {
    id: question.id || newId(),

    question_type:
      question.question_type ||
      "single_choice",

    prompt: question.prompt || "",

    options: (
      question.options || []
    ).map((option, index) =>
      typeof option === "object"
        ? {
            id: String(
              option.id ??
                option.value ??
                `opt-${index}`
            ),
            label:
              option.label ??
              option.value ??
              "",
          }
        : {
            id: `opt-${index}`,
            label: String(option),
          }
    ),

    correct_answers: (
      question.correct_answers || []
    ).map(String),

    marks: Number(
      question.marks || 1
    ),

    feedback_correct:
      feedback.correct ||
      question.feedback_correct ||
      "",

    feedback_incorrect:
      feedback.incorrect ||
      question.feedback_incorrect ||
      "",

    explanation:
      feedback.explanation ||
      question.explanation ||
      "",
  };
}

function toApiQuestion(question) {
  return {
    id: question.id,

    question_type:
      question.question_type,

    prompt: question.prompt,

    options: question.options.map(
      (option) => ({
        id: option.id,
        label: option.label,
      })
    ),

    correct_answers:
      question.correct_answers,

    marks: Number(
      question.marks || 1
    ),

    feedback_correct:
      question.feedback_correct ||
      null,

    feedback_incorrect:
      question.feedback_incorrect ||
      null,

    explanation:
      question.explanation || null,
  };
}

export default function LMSAssessmentEditor() {
  const { activityId } =
    useParams();

  const navigate = useNavigate();

  const [
    assessment,
    setAssessment,
  ] = useState(null);

  const [banks, setBanks] =
    useState([]);

  const [
    bankQuestions,
    setBankQuestions,
  ] = useState([]);

  const [
    selectedBank,
    setSelectedBank,
  ] = useState("");

  const [
    selectedBankQuestion,
    setSelectedBankQuestion,
  ] = useState("");

  const [loading, setLoading] =
    useState(true);

  const [busy, setBusy] =
    useState(false);

  const [error, setError] =
    useState("");

  const [notice, setNotice] =
    useState("");

  const [form, setForm] =
    useState(null);

  const editable =
    assessment?.version_status ===
    "draft";

  const load = async () => {
    setError("");

    try {
      const me = await lmsApi.get(
        "/auth/me"
      );

      if (
        me.data
          .require_password_change
      ) {
        navigate(
          "/learn/change-password",
          {
            replace: true,
          }
        );

        return;
      }

      if (
        !me.data.is_platform_admin
      ) {
        navigate("/learn", {
          replace: true,
        });

        return;
      }

      const [
        assessmentResponse,
        banksResponse,
      ] = await Promise.all([
        lmsApi.get(
          `/assessments/activities/${activityId}`
        ),
        lmsApi.get(
          "/assessments/banks"
        ),
      ]);

      const data =
        assessmentResponse.data;

      setAssessment(data);
      setBanks(banksResponse.data);

      setForm({
        title: data.title,

        required: data.required,

        settings: {
          pass_mark:
            data.settings.pass_mark ??
            80,

          max_attempts:
            data.settings
              .max_attempts ?? 3,

          randomise_questions:
            data.settings
              .randomise_questions ===
            true,

          question_limit:
            data.settings
              .question_limit ?? "",

          randomise_options:
            data.settings
              .randomise_options ===
            true,

          feedback_mode:
            data.settings
              .feedback_mode ||
            "after_each_attempt",

          require_pass:
            data.settings
              .require_pass !== false,
        },

        questions: (
          data.questions || []
        ).map(fromApiQuestion),
      });

      const firstBank =
        banksResponse.data[0]?.id ||
        "";

      setSelectedBank(
        (current) =>
          current || firstBank
      );
    } catch (err) {
      if (
        err.response?.status === 401
      ) {
        navigate("/learn/login", {
          replace: true,
        });

        return;
      }

      setError(
        err.response?.data?.detail ||
          "We could not load this assessment."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityId]);

  useEffect(() => {
    if (!selectedBank) {
      setBankQuestions([]);
      setSelectedBankQuestion("");
      return;
    }

    lmsApi
      .get(
        `/assessments/banks/${selectedBank}/questions`
      )
      .then((response) => {
        const active =
          response.data.filter(
            (item) =>
              item.status === "active"
          );

        setBankQuestions(active);

        setSelectedBankQuestion(
          active[0]?.id || ""
        );
      })
      .catch(() => {
        setBankQuestions([]);
        setSelectedBankQuestion("");
      });
  }, [selectedBank]);

  const totalMarks = useMemo(
    () =>
      (
        form?.questions || []
      ).reduce(
        (sum, question) =>
          sum +
          Number(
            question.marks || 0
          ),
        0
      ),
    [form]
  );

  const updateQuestion = (
    questionId,
    updater
  ) => {
    setForm((current) => ({
      ...current,

      questions:
        current.questions.map(
          (question) =>
            question.id ===
            questionId
              ? updater(question)
              : question
        ),
    }));
  };

  const changeType = (
    question,
    type
  ) => {
    const replacement =
      blankQuestion(type);

    return {
      ...replacement,

      id: question.id,

      prompt: question.prompt,

      marks: question.marks,

      feedback_correct:
        question.feedback_correct,

      feedback_incorrect:
        question.feedback_incorrect,

      explanation:
        question.explanation,
    };
  };

  const toggleCorrect = (
    question,
    optionId
  ) => {
    if (
      question.question_type ===
      "multiple_response"
    ) {
      const next = new Set(
        question.correct_answers
      );

      if (next.has(optionId)) {
        next.delete(optionId);
      } else {
        next.add(optionId);
      }

      return {
        ...question,
        correct_answers:
          Array.from(next),
      };
    }

    return {
      ...question,
      correct_answers: [optionId],
    };
  };

  const addOption = (question) => ({
    ...question,

    options: [
      ...question.options,
      {
        id: newId("opt"),
        label: "",
      },
    ],
  });

  const removeOption = (
    question,
    optionId
  ) => {
    if (
      question.options.length <= 2
    ) {
      return question;
    }

    return {
      ...question,

      options:
        question.options.filter(
          (option) =>
            option.id !== optionId
        ),

      correct_answers:
        question.correct_answers.filter(
          (id) => id !== optionId
        ),
    };
  };

  const save = async () => {
    if (!form) {
      return;
    }

    setBusy(true);
    setError("");
    setNotice("");

    try {
      if (
        !form.questions.length
      ) {
        throw new Error(
          "Add at least one question before saving."
        );
      }

      const response =
        await lmsApi.put(
          `/assessments/activities/${activityId}`,
          {
            title: form.title,

            required:
              form.required,

            settings: {
              pass_mark: Number(
                form.settings
                  .pass_mark
              ),

              max_attempts:
                Number(
                  form.settings
                    .max_attempts
                ),

              randomise_questions:
                form.settings
                  .randomise_questions,

              question_limit:
                form.settings
                  .question_limit ===
                ""
                  ? null
                  : Number(
                      form.settings
                        .question_limit
                    ),

              randomise_options:
                form.settings
                  .randomise_options,

              feedback_mode:
                form.settings
                  .feedback_mode,

              require_pass:
                form.settings
                  .require_pass,
            },

            questions:
              form.questions.map(
                toApiQuestion
              ),
          }
        );

      setAssessment(
        response.data
      );

      setNotice(
        "Assessment saved. Correct answers remain server-side and will not be exposed to learners."
      );
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          "The assessment could not be saved."
      );
    } finally {
      setBusy(false);
    }
  };

  const addFromBank = () => {
    const item =
      bankQuestions.find(
        (question) =>
          question.id ===
          selectedBankQuestion
      );

    if (!item) {
      return;
    }

    const snapshot =
      fromApiQuestion({
        ...item,
        id: newId(),
      });

    setForm((current) => ({
      ...current,

      questions: [
        ...current.questions,
        snapshot,
      ],
    }));

    setNotice(
      "Question copied from the bank into this course version. Future bank edits will not alter this assessment snapshot."
    );
  };

  const saveQuestionToBank =
    async (question) => {
      if (!selectedBank) {
        setError(
          "Create or select a question bank first."
        );

        return;
      }

      setBusy(true);
      setError("");

      try {
        await lmsApi.post(
          `/assessments/banks/${selectedBank}/questions`,
          {
            question:
              toApiQuestion({
                ...question,
                id: null,
              }),

            tags: [],
          }
        );

        const response =
          await lmsApi.get(
            `/assessments/banks/${selectedBank}/questions`
          );

        const active =
          response.data.filter(
            (item) =>
              item.status === "active"
          );

        setBankQuestions(active);

        setSelectedBankQuestion(
          active[
            active.length - 1
          ]?.id || ""
        );

        setNotice(
          "Question saved to the reusable UKTDA question bank."
        );
      } catch (err) {
        setError(
          err.response?.data
            ?.detail ||
            "The question could not be saved to the bank."
        );
      } finally {
        setBusy(false);
      }
    };

  if (loading) {
    return (
      <section className="min-h-screen bg-slate-50 py-20">
        <div className="container-rail text-center text-slate-600">
          Loading assessment editor…
        </div>
      </section>
    );
  }

  if (!assessment || !form) {
    return (
      <section className="min-h-screen bg-slate-50 py-20">
        <div className="container-rail">
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-800">
            {error ||
              "Assessment not found."}
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <SEO
        title={`${assessment.title} | TRAC-Learn Assessment`}
        description="Secure assessment authoring."
        path={`/learn/admin/assessments/${activityId}`}
      />

      <section className="bg-navy py-9 text-white md:py-12">
        <div className="container-rail">
          <button
            type="button"
            onClick={() =>
              navigate(
                "/learn/admin/assessments"
              )
            }
            className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Assessment catalogue
          </button>

          <div className="mt-6 flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
            <div>
              <div className="flex items-center gap-2 text-safety">
                <ShieldCheck className="h-5 w-5" />

                <p className="label-eyebrow">
                  {
                    assessment.course_title
                  }{" "}
                  · Version{" "}
                  {
                    assessment.version_number
                  }
                </p>
              </div>

              <h1 className="mt-3 font-display text-4xl font-bold md:text-5xl">
                {form.title}
              </h1>

              <p className="mt-2 text-white/70">
                {
                  assessment.module_title
                }{" "}
                ·{" "}
                {
                  form.questions.length
                }{" "}
                questions · {totalMarks}{" "}
                marks
              </p>
            </div>

            {editable ? (
              <button
                type="button"
                onClick={save}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-lg bg-safety px-5 py-3 text-sm font-bold disabled:opacity-50"
              >
                <Save className="h-4 w-4" />

                {busy
                  ? "Saving…"
                  : "Save assessment"}
              </button>
            ) : (
              <div className="inline-flex items-center gap-2 rounded-lg bg-emerald-400/15 px-4 py-3 text-sm font-semibold text-emerald-200">
                <CheckCircle2 className="h-4 w-4" />
                Published and immutable
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="min-h-[70vh] bg-slate-50 py-8 md:py-12">
        <div className="container-rail">
          {error && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              {error}
            </div>
          )}

          {notice && (
            <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
              {notice}
            </div>
          )}

          <div className="grid gap-8 xl:grid-cols-[330px_1fr]">
            <aside className="space-y-6">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="label-eyebrow text-safety">
                  Assessment settings
                </p>

                <label className="mt-4 block text-sm font-semibold text-navy">
                  Title

                  <input
                    value={form.title}
                    onChange={(event) =>
                      setForm(
                        (current) => ({
                          ...current,
                          title:
                            event.target
                              .value,
                        })
                      )
                    }
                    disabled={!editable}
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 disabled:bg-slate-100"
                  />
                </label>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <label className="text-sm font-semibold text-navy">
                    Pass mark

                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={
                        form.settings
                          .pass_mark
                      }
                      onChange={(event) =>
                        setForm(
                          (current) => ({
                            ...current,

                            settings: {
                              ...current.settings,

                              pass_mark:
                                event
                                  .target
                                  .value,
                            },
                          })
                        )
                      }
                      disabled={!editable}
                      className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 disabled:bg-slate-100"
                    />
                  </label>

                  <label className="text-sm font-semibold text-navy">
                    Attempts

                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={
                        form.settings
                          .max_attempts
                      }
                      onChange={(event) =>
                        setForm(
                          (current) => ({
                            ...current,

                            settings: {
                              ...current.settings,

                              max_attempts:
                                event
                                  .target
                                  .value,
                            },
                          })
                        )
                      }
                      disabled={!editable}
                      className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 disabled:bg-slate-100"
                    />
                  </label>
                </div>

                <label className="mt-4 block text-sm font-semibold text-navy">
                  Questions per attempt{" "}
                  <span className="font-normal text-slate-400">
                    blank = all
                  </span>

                  <input
                    type="number"
                    min="1"
                    max={Math.max(
                      1,
                      form.questions.length
                    )}
                    value={
                      form.settings
                        .question_limit
                    }
                    onChange={(event) =>
                      setForm(
                        (current) => ({
                          ...current,

                          settings: {
                            ...current.settings,

                            question_limit:
                              event.target
                                .value,
                          },
                        })
                      )
                    }
                    disabled={!editable}
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 disabled:bg-slate-100"
                  />
                </label>

                <label className="mt-4 block text-sm font-semibold text-navy">
                  Feedback timing

                  <select
                    value={
                      form.settings
                        .feedback_mode
                    }
                    onChange={(event) =>
                      setForm(
                        (current) => ({
                          ...current,

                          settings: {
                            ...current.settings,

                            feedback_mode:
                              event.target
                                .value,
                          },
                        })
                      )
                    }
                    disabled={!editable}
                    className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 disabled:bg-slate-100"
                  >
                    <option value="after_each_attempt">
                      After every attempt
                    </option>

                    <option value="after_pass">
                      After passing
                    </option>

                    <option value="after_final_attempt">
                      After pass/final attempt
                    </option>

                    <option value="none">
                      No question feedback
                    </option>
                  </select>
                </label>

                <div className="mt-5 space-y-3 text-sm text-navy">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={
                        form.settings
                          .randomise_questions
                      }
                      onChange={(event) =>
                        setForm(
                          (current) => ({
                            ...current,

                            settings: {
                              ...current.settings,

                              randomise_questions:
                                event.target
                                  .checked,
                            },
                          })
                        )
                      }
                      disabled={!editable}
                    />

                    Randomise question order
                  </label>

                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={
                        form.settings
                          .randomise_options
                      }
                      onChange={(event) =>
                        setForm(
                          (current) => ({
                            ...current,

                            settings: {
                              ...current.settings,

                              randomise_options:
                                event.target
                                  .checked,
                            },
                          })
                        )
                      }
                      disabled={!editable}
                    />

                    Randomise answer options
                  </label>

                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={
                        form.settings
                          .require_pass
                      }
                      onChange={(event) =>
                        setForm(
                          (current) => ({
                            ...current,

                            settings: {
                              ...current.settings,

                              require_pass:
                                event.target
                                  .checked,
                            },
                          })
                        )
                      }
                      disabled={!editable}
                    />

                    Passing required for completion
                  </label>

                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={
                        form.required
                      }
                      onChange={(event) =>
                        setForm(
                          (current) => ({
                            ...current,

                            required:
                              event.target
                                .checked,
                          })
                        )
                      }
                      disabled={!editable}
                    />

                    Assessment required in course
                  </label>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <Library className="h-5 w-5 text-safety" />

                  <p className="font-bold text-navy">
                    Question bank
                  </p>
                </div>

                {banks.length ? (
                  <>
                    <select
                      value={selectedBank}
                      onChange={(event) =>
                        setSelectedBank(
                          event.target
                            .value
                        )
                      }
                      className="mt-4 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5"
                    >
                      {banks.map(
                        (bank) => (
                          <option
                            key={bank.id}
                            value={bank.id}
                          >
                            {bank.title}
                          </option>
                        )
                      )}
                    </select>

                    <select
                      value={
                        selectedBankQuestion
                      }
                      onChange={(event) =>
                        setSelectedBankQuestion(
                          event.target
                            .value
                        )
                      }
                      className="mt-3 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5"
                    >
                      <option value="">
                        Select a question
                      </option>

                      {bankQuestions.map(
                        (question) => (
                          <option
                            key={
                              question.id
                            }
                            value={
                              question.id
                            }
                          >
                            {question.prompt.slice(
                              0,
                              70
                            )}
                          </option>
                        )
                      )}
                    </select>

                    <button
                      type="button"
                      onClick={addFromBank}
                      disabled={
                        !editable ||
                        !selectedBankQuestion
                      }
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-navy px-4 py-2.5 text-sm font-bold text-navy disabled:opacity-40"
                    >
                      <CopyPlus className="h-4 w-4" />
                      Copy into assessment
                    </button>
                  </>
                ) : (
                  <p className="mt-3 text-sm text-slate-500">
                    Create a question bank from the assessment catalogue to
                    reuse questions across courses.
                  </p>
                )}
              </div>
            </aside>

            <main>
              <div className="flex items-end justify-between gap-4">
                <div>
                  <p className="label-eyebrow text-safety">
                    Question set
                  </p>

                  <h2 className="mt-2 font-display text-3xl font-bold text-navy">
                    Assessment questions
                  </h2>
                </div>

                {editable && (
                  <button
                    type="button"
                    onClick={() =>
                      setForm(
                        (current) => ({
                          ...current,

                          questions: [
                            ...current.questions,
                            blankQuestion(),
                          ],
                        })
                      )
                    }
                    className="inline-flex items-center gap-2 rounded-lg bg-navy px-4 py-3 text-sm font-bold text-white"
                  >
                    <Plus className="h-4 w-4" />
                    Add question
                  </button>
                )}
              </div>

              <div className="mt-6 space-y-5">
                {form.questions.map(
                  (
                    question,
                    questionIndex
                  ) => (
                    <article
                      key={question.id}
                      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6"
                    >
                      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
                        <div className="flex-1">
                          <p className="text-xs font-bold uppercase tracking-wide text-safety">
                            Question{" "}
                            {questionIndex +
                              1}
                          </p>

                          <textarea
                            value={
                              question.prompt
                            }
                            onChange={(event) =>
                              updateQuestion(
                                question.id,
                                (
                                  current
                                ) => ({
                                  ...current,

                                  prompt:
                                    event
                                      .target
                                      .value,
                                })
                              )
                            }
                            disabled={
                              !editable
                            }
                            rows={3}
                            className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 text-lg font-semibold text-navy disabled:bg-slate-50"
                            placeholder="Question or scenario prompt"
                          />
                        </div>

                        {editable && (
                          <button
                            type="button"
                            onClick={() =>
                              setForm(
                                (
                                  current
                                ) => ({
                                  ...current,

                                  questions:
                                    current.questions.filter(
                                      (
                                        item
                                      ) =>
                                        item.id !==
                                        question.id
                                    ),
                                })
                              )
                            }
                            className="rounded-lg border border-red-200 p-2 text-red-600"
                            title="Delete question"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>

                      <div className="mt-5 grid gap-4 md:grid-cols-3">
                        <label className="text-sm font-semibold text-navy">
                          Type

                          <select
                            value={
                              question.question_type
                            }
                            onChange={(event) =>
                              updateQuestion(
                                question.id,
                                (
                                  current
                                ) =>
                                  changeType(
                                    current,
                                    event
                                      .target
                                      .value
                                  )
                              )
                            }
                            disabled={
                              !editable
                            }
                            className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 disabled:bg-slate-100"
                          >
                            <option value="single_choice">
                              Single choice
                            </option>

                            <option value="multiple_response">
                              Multiple response
                            </option>

                            <option value="true_false">
                              True / false
                            </option>

                            <option value="scenario">
                              Scenario decision
                            </option>
                          </select>
                        </label>

                        <label className="text-sm font-semibold text-navy">
                          Marks

                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={
                              question.marks
                            }
                            onChange={(event) =>
                              updateQuestion(
                                question.id,
                                (
                                  current
                                ) => ({
                                  ...current,

                                  marks:
                                    event
                                      .target
                                      .value,
                                })
                              )
                            }
                            disabled={
                              !editable
                            }
                            className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 disabled:bg-slate-100"
                          />
                        </label>

                        <div className="flex items-end">
                          {editable &&
                            banks.length >
                              0 && (
                              <button
                                type="button"
                                onClick={() =>
                                  saveQuestionToBank(
                                    question
                                  )
                                }
                                disabled={
                                  busy
                                }
                                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 px-3 py-2.5 text-sm font-semibold text-navy"
                              >
                                <Library className="h-4 w-4" />
                                Save to selected bank
                              </button>
                            )}
                        </div>
                      </div>

                      <div className="mt-5">
                        <p className="text-sm font-bold text-navy">
                          Answer options{" "}
                          <span className="font-normal text-slate-400">
                            select the correct response(s)
                          </span>
                        </p>

                        <div className="mt-3 space-y-3">
                          {question.options.map(
                            (
                              option,
                              optionIndex
                            ) => {
                              const correct =
                                question.correct_answers.includes(
                                  option.id
                                );

                              const multi =
                                question.question_type ===
                                "multiple_response";

                              return (
                                <div
                                  key={
                                    option.id
                                  }
                                  className="flex items-center gap-3 rounded-xl border border-slate-200 p-3"
                                >
                                  <input
                                    type={
                                      multi
                                        ? "checkbox"
                                        : "radio"
                                    }
                                    name={`correct-${question.id}`}
                                    checked={
                                      correct
                                    }
                                    onChange={() =>
                                      updateQuestion(
                                        question.id,
                                        (
                                          current
                                        ) =>
                                          toggleCorrect(
                                            current,
                                            option.id
                                          )
                                      )
                                    }
                                    disabled={
                                      !editable
                                    }
                                    aria-label={`Mark option ${
                                      optionIndex +
                                      1
                                    } correct`}
                                  />

                                  <input
                                    value={
                                      option.label
                                    }
                                    onChange={(event) =>
                                      updateQuestion(
                                        question.id,
                                        (
                                          current
                                        ) => ({
                                          ...current,

                                          options:
                                            current.options.map(
                                              (
                                                item
                                              ) =>
                                                item.id ===
                                                option.id
                                                  ? {
                                                      ...item,

                                                      label:
                                                        event
                                                          .target
                                                          .value,
                                                    }
                                                  : item
                                            ),
                                        })
                                      )
                                    }
                                    disabled={
                                      !editable ||
                                      question.question_type ===
                                        "true_false"
                                    }
                                    className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 disabled:bg-slate-100"
                                    placeholder={`Option ${
                                      optionIndex +
                                      1
                                    }`}
                                  />

                                  {editable &&
                                    question.question_type !==
                                      "true_false" && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          updateQuestion(
                                            question.id,
                                            (
                                              current
                                            ) =>
                                              removeOption(
                                                current,
                                                option.id
                                              )
                                          )
                                        }
                                        className="p-2 text-red-500"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    )}
                                </div>
                              );
                            }
                          )}
                        </div>

                        {editable &&
                          question.question_type !==
                            "true_false" && (
                            <button
                              type="button"
                              onClick={() =>
                                updateQuestion(
                                  question.id,
                                  addOption
                                )
                              }
                              className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-safety"
                            >
                              <Plus className="h-4 w-4" />
                              Add option
                            </button>
                          )}
                      </div>

                      <div className="mt-6 grid gap-4 md:grid-cols-2">
                        <label className="text-sm font-semibold text-navy">
                          Feedback when correct

                          <textarea
                            value={
                              question.feedback_correct
                            }
                            onChange={(event) =>
                              updateQuestion(
                                question.id,
                                (
                                  current
                                ) => ({
                                  ...current,

                                  feedback_correct:
                                    event
                                      .target
                                      .value,
                                })
                              )
                            }
                            disabled={
                              !editable
                            }
                            rows={2}
                            className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 disabled:bg-slate-100"
                          />
                        </label>

                        <label className="text-sm font-semibold text-navy">
                          Feedback when incorrect

                          <textarea
                            value={
                              question.feedback_incorrect
                            }
                            onChange={(event) =>
                              updateQuestion(
                                question.id,
                                (
                                  current
                                ) => ({
                                  ...current,

                                  feedback_incorrect:
                                    event
                                      .target
                                      .value,
                                })
                              )
                            }
                            disabled={
                              !editable
                            }
                            rows={2}
                            className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 disabled:bg-slate-100"
                          />
                        </label>

                        <label className="text-sm font-semibold text-navy md:col-span-2">
                          Explanation

                          <textarea
                            value={
                              question.explanation
                            }
                            onChange={(event) =>
                              updateQuestion(
                                question.id,
                                (
                                  current
                                ) => ({
                                  ...current,

                                  explanation:
                                    event
                                      .target
                                      .value,
                                })
                              )
                            }
                            disabled={
                              !editable
                            }
                            rows={3}
                            className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 disabled:bg-slate-100"
                          />
                        </label>
                      </div>
                    </article>
                  )
                )}

                {!form.questions
                  .length && (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
                    <h3 className="font-display text-xl font-bold text-navy">
                      No questions yet
                    </h3>

                    <p className="mt-2 text-slate-600">
                      Add a question or copy one from a question bank.
                    </p>
                  </div>
                )}
              </div>
            </main>
          </div>
        </div>
      </section>
    </>
  );
}